/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { useState } from "react";
import { ResultActions } from "@/components/tools/result-actions";

const coverageRates: Record<string, number> = {
  "Interior Wall (smooth)": 400,
  "Interior Wall (textured)": 300,
  "Exterior (wood siding)": 350,
  "Exterior (stucco)": 200,
  "Exterior (brick)": 200,
  "Ceiling (smooth)": 400,
  "Ceiling (textured)": 300,
  "Trim / Doors": 400,
  "Deck / Fence": 250,
  "Concrete / Masonry": 200,
};

export function PaintCalculatorClient() {
  const [mode, setMode] = useState<"room" | "area">("room");
  const [roomLength, setRoomLength] = useState("");
  const [roomWidth, setRoomWidth] = useState("");
  const [wallHeight, setWallHeight] = useState("9");
  const [totalArea, setTotalArea] = useState("");
  const [numDoors, setNumDoors] = useState("1");
  const [numWindows, setNumWindows] = useState("2");
  const [coats, setCoats] = useState("2");
  const [surfaceType, setSurfaceType] = useState("Interior Wall (smooth)");
  const [pricePerGallon, setPricePerGallon] = useState("");
  const [needPrimer, setNeedPrimer] = useState(false);

  const doorArea = 21; // ~3x7 ft
  const windowArea = 15; // ~3x5 ft

  let paintableArea = 0;
  if (mode === "room") {
    const l = parseFloat(roomLength) || 0;
    const w = parseFloat(roomWidth) || 0;
    const h = parseFloat(wallHeight) || 9;
    const wallArea = 2 * (l + w) * h;
    const openings = (parseFloat(numDoors) || 0) * doorArea + (parseFloat(numWindows) || 0) * windowArea;
    paintableArea = Math.max(0, wallArea - openings);
  } else {
    const ta = totalArea;
    paintableArea = parseFloat(ta) || 0;
  }

  const numCoats = parseFloat(coats) || 2;
  const coverage = coverageRates[surfaceType] || 400;
  const totalCoverage = paintableArea * numCoats;
  const gallonsNeeded = totalCoverage / coverage;
  const gallonsRounded = Math.ceil(gallonsNeeded);
  const primerGallons = needPrimer ? Math.ceil(paintableArea / 300) : 0;
  const ppg = pricePerGallon;
  const pIn = ppg;
  const price = parseFloat(pIn) || 0;
  const totalGallons = gallonsRounded + primerGallons;

  const hasResult = paintableArea > 0;

  const fmt = (n: number) => n.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 1 });
  const fmtCost = (n: number) => n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="mt-8 rounded-lg card-premium p-6">
      {/* Mode toggle */}
      <div className="mb-4 flex gap-2">
        <button
          onClick={() => setMode("room")}
          className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${mode === "room" ? "bg-foreground text-background" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}
        >
          Room Dimensions
        </button>
        <button
          onClick={() => setMode("area")}
          className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${mode === "area" ? "bg-foreground text-background" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}
        >
          Total Area
        </button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {mode === "room" ? (
          <>
            <div>
              <label className="block text-sm font-medium text-foreground">Room Length (ft)</label>
              <input type="number" value={roomLength} onChange={(e) => setRoomLength(e.target.value)} placeholder="12" className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground">Room Width (ft)</label>
              <input type="number" value={roomWidth} onChange={(e) => setRoomWidth(e.target.value)} placeholder="10" className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground">Wall Height (ft)</label>
              <input type="number" value={wallHeight} onChange={(e) => setWallHeight(e.target.value)} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground">Doors</label>
                <input type="number" value={numDoors} onChange={(e) => setNumDoors(e.target.value)} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground">Windows</label>
                <input type="number" value={numWindows} onChange={(e) => setNumWindows(e.target.value)} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
              </div>
            </div>
          </>
        ) : (
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-foreground">Total Paintable Area (sq ft)</label>
            <input type="number" value={totalArea} onChange={(e) => setTotalArea(e.target.value)} placeholder="500" className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-foreground">Surface Type</label>
          <select value={surfaceType} onChange={(e) => setSurfaceType(e.target.value)} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary">
            {Object.entries(coverageRates).map(([name, rate]) => (
              <option key={name} value={name}>{name} ({rate} sq ft/gal)</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground">Number of Coats</label>
          <input type="number" value={coats} onChange={(e) => setCoats(e.target.value)} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground">Price per Gallon (optional)</label>
          <input type="number" value={pricePerGallon} onChange={(e) => setPricePerGallon(e.target.value)} placeholder="45.00" className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus-visible:border-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input type="checkbox" checked={needPrimer} onChange={(e) => setNeedPrimer(e.target.checked)} className="h-4 w-4 rounded border-input text-primary" />
            Include primer coat
          </label>
        </div>
      </div>

      {hasResult && (
        <>
          <div className="mt-6 grid gap-4 rounded-lg bg-muted/50 p-4 sm:grid-cols-4">
            <div>
              <div className="text-sm text-muted-foreground">Paintable Area</div>
              <div className="text-2xl font-bold text-foreground">{fmt(paintableArea)} sq ft</div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Paint Needed</div>
              <div className="text-2xl font-bold text-secondary">{gallonsRounded} gal</div>
              <div className="text-xs text-muted-foreground">{numCoats} coat{numCoats !== 1 ? "s" : ""} @ {coverage} sq ft/gal</div>
            </div>
            {needPrimer && (
              <div>
                <div className="text-sm text-muted-foreground">Primer Needed</div>
                <div className="text-2xl font-bold text-secondary">{primerGallons} gal</div>
              </div>
            )}
            {price > 0 && (
              <div>
                <div className="text-sm text-muted-foreground">Estimated Cost</div>
                <div className="text-2xl font-bold text-foreground">${fmtCost(totalGallons * price)}</div>
                <div className="text-xs text-muted-foreground">{totalGallons} gal total</div>
              </div>
            )}
          </div>
          <ResultActions
            toolSlug="paint-calculator"
            inputs={{ mode, roomLength, roomWidth, wallHeight, totalArea, numDoors, numWindows, coats, surfaceType, pricePerGallon, needPrimer }}
            outputs={{ paintableArea, gallonsNeeded: gallonsRounded, primerGallons, totalGallons, estimatedCost: price > 0 ? gallonsRounded * price : undefined }}
          />
        </>
      )}
    </div>
  );
}
